import { useNode } from "@craftjs/core";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { OEmbedError } from "src/types";

import { Button } from "@/components/button";
import { Input } from "@/components/shared/forms";
import { Spinner } from "@/components/shared/spinner";
import logger from "@/utils/logger";
import {
  fetchVideoMetadata,
  OEmbedErrorTranslationKeys,
} from "@/utils/o-embed";
import isValidUrlProtocol from "@/utils/validate-url-protocol";

import type { VideoGridProps, videoProps } from "../video-grid-component";

export const VideoURLSetting = () => {
  const {
    actions: { setProp },
  } = useNode();

  const [desiredUrl, setDesiredUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isEmbedding, setIsEmbedding] = useState<boolean>(false);
  const { t } = useTranslation();

  const handleTextChange = useCallback((event) => {
    setDesiredUrl(event.currentTarget.value);
  }, []);

  const handleEmbedPress = useCallback(async () => {
    setHasError(false);
    const trimmedUrl = desiredUrl?.trim();
    if (!trimmedUrl) {
      return;
    }

    if (!isValidUrlProtocol(trimmedUrl)) {
      setHasError(true);
      setErrorMessage(t("preference-panel.invalid-url-error"));
      return;
    }

    const newVideoProps: videoProps = {
      kind: "embed",
      url: new URL(trimmedUrl).toString(),
      id: new Date().getTime().toString(),
      title: trimmedUrl,
      thumbnail: "/canvas/shared/images/gray-background-embedded.png",
    };

    try {
      setIsEmbedding(true);
      const oEmbedData = await fetchVideoMetadata(newVideoProps.url);

      if ("error" in oEmbedData) {
        setHasError(true);
        setErrorMessage(
          t(`preference-panel.${OEmbedErrorTranslationKeys[oEmbedData.error]}`)
        );
      } else {
        const { title, thumbnail } = oEmbedData;
        newVideoProps.title = title;
        newVideoProps.thumbnail = thumbnail;

        setProp((props: VideoGridProps) => {
          setHasError(false);
          props.videoList.push(newVideoProps);
        });

        setDesiredUrl(null);
        setIsEmbedding(false);
      }
    } catch (e) {
      logger.error(new OEmbedError("Error during embedding a video"), {
        details: JSON.stringify(e),
      });
      setHasError(true);
      setErrorMessage(t("preference-panel.error-fetching-video-data"));
    } finally {
      setIsEmbedding(false);
    }
  }, [desiredUrl, setProp, t]);

  return (
    <div className="grid gap-2">
      <Input
        inputClassName="h-8 text-xs"
        name={t("preference-panel.video-url")}
        label={t("preference-panel.video-url")}
        value={desiredUrl ?? ""}
        onChange={handleTextChange}
        error={hasError ? errorMessage : undefined}
      />
      <div className="text-xs text-gray-600">
        <p>{t("preference-panel.add-a-video")}</p>
        <p>{t("preference-panel.supported-platforms")}</p>
      </div>
      <Button size="small" onClick={handleEmbedPress} disabled={isEmbedding}>
        {isEmbedding ? <Spinner /> : t("preference-panel.embed-video")}
      </Button>
    </div>
  );
};
